import get from "lodash/get";

import { Theme, useTheme } from "@mui/material";

import { colors } from "@themes/utils/colors";

type Foundations = (typeof colors)["01-foundations"];
type Tokens = (typeof colors)["02-tokens"]["light"];
type Colors = (typeof colors)["color"];
type Gradients = (typeof colors)["gradient"];

export type GetterType = {
  foundations: Foundations;
  tokens: Tokens;
  colors: Colors;
  gradients: Gradients;
};

export type OutputColor = { type: string; value: string };

interface GradientStop {
  position: number;
  color: string;
}

interface GradientValue {
  gradientType: string;
  rotation: number;
  stops: GradientStop[];
}

export interface OutputGradient {
  description: string;
  type: string;
  value: GradientValue;
}

export const getColor = (
  f: (x: GetterType) => OutputColor | OutputGradient,
  theme: Theme,
): string => {
  const themeMode = theme.palette.mode;

  const output = f({
    tokens:
      themeMode === "light"
        ? colors["02-tokens"].light
        : colors["02-tokens"].dark,
    foundations: colors["01-foundations"],
    colors: colors.color,
    gradients: colors.gradient,
  });

  if (output.type === "custom-gradient") {
    const value = output.value as GradientValue;
    return `
      linear-gradient(
        ${value.rotation}deg,
        ${value.stops.map(stop => `${stop.color} ${stop.position * 100}%`).join(", ")}
      )
    `;
  } else {
    const value = output.value as string;
    const finalValue = value.includes(".light")
      ? value.replace(".light", "")
      : value.includes(".dark")
        ? value.replace(".dark", "")
        : value;

    if (finalValue.startsWith("{") && finalValue.endsWith("}")) {
      return get(colors, finalValue.substring(1, finalValue.length - 1)).value;
    } else {
      return finalValue;
    }
  }
};

export const useColors = () => {
  const theme = useTheme();

  return {
    getColor: (f: (x: GetterType) => OutputColor | OutputGradient) =>
      getColor(f, theme),
  };
};
